/**
 * Generic accumulation functions for peripheral dissociation/association.
 * Replaces the 3× repeated push/merge-by-shopId patterns in processElement.
 */

export type AccumulationBaseParams = {
    userID: number;
    franchiseId: string;
    appId: string;
    operatorId: string;
    uuidUser: string | null;
};

/**
 * Accumulates dissociation data for a list of peripheral IDs.
 * For each peripheral ID, either creates a new entry grouped by shopId
 * or merges into an existing entry.
 *
 * Mutates targetArray in place.
 */
export function accumulateDissociation(
    targetArray: any[],
    peripheralIds: any[],
    subRows: any[],
    element: any,
    currentShopId: string,
    baseParams: AccumulationBaseParams,
    deviceLabel: string
): void {
    for (const peripheralId of peripheralIds) {
        if (peripheralId === undefined) continue;

        let name = "";
        subRows.forEach((el: any) => {
            if (el.peripheralId === peripheralId) {
                name = el[deviceLabel];
            }
        });

        const indexInData = targetArray.findIndex(
            (el: any) => el.shopId === currentShopId
        );

        if (indexInData === -1) {
            targetArray.push({
                userId: baseParams.userID,
                shopId: currentShopId,
                franchiseId: baseParams.franchiseId,
                appId: baseParams.appId,
                ids: { [element.appId]: [peripheralId] },
                appId_children: [element.appId],
                peripheralName: { [element.appId]: [name] },
                operatorId: baseParams.operatorId,
                uuidUser: baseParams.uuidUser,
                isArchived: true,
                applicationName: [element["Application"]],
            });
        } else {
            targetArray[indexInData] = {
                ...targetArray[indexInData],
                ids: {
                    ...targetArray[indexInData].ids,
                    [element.appId]: [
                        ...(targetArray[indexInData].ids[element.appId] || []),
                        peripheralId,
                    ],
                },
                peripheralName: {
                    ...targetArray[indexInData].peripheralName,
                    [element.appId]: [
                        ...(targetArray[indexInData].peripheralName?.[
                            element.appId
                        ] || []),
                        name,
                    ],
                },
                appId_children: targetArray[
                    indexInData
                ].appId_children?.includes(element.appId)
                    ? targetArray[indexInData].appId_children
                    : [
                          ...(targetArray[indexInData].appId_children || []),
                          element.appId,
                      ],
                applicationName: targetArray[
                    indexInData
                ].applicationName?.includes(element["Application"])
                    ? targetArray[indexInData].applicationName
                    : [
                          ...(targetArray[indexInData].applicationName || []),
                          element["Application"],
                      ],
            };
        }
    }
}

/**
 * Accumulates association data for a set of peripheral objects.
 * Either creates a new entry grouped by shopId or merges into an existing entry.
 *
 * Does nothing if peripheralObjects is empty.
 * Mutates targetArray in place.
 */
export function accumulateAssociation(
    targetArray: any[],
    peripheralObjects: Record<string, any>,
    element: any,
    currentShopId: string,
    baseParams: AccumulationBaseParams
): void {
    if (Object.keys(peripheralObjects).length === 0) return;

    const indexInData = targetArray.findIndex(
        (el: any) => el.shopId === currentShopId
    );

    if (indexInData === -1) {
        targetArray.push({
            userId: baseParams.userID,
            shopId: currentShopId,
            franchiseId: baseParams.franchiseId,
            appId: baseParams.appId,
            operatorId: baseParams.operatorId,
            uuidUser: baseParams.uuidUser,
            appId_children: [element["appId"]],
            applicationName: [element["Application"]],
            data: peripheralObjects,
        });
    } else {
        targetArray[indexInData] = {
            ...targetArray[indexInData],
            appId_children: targetArray[
                indexInData
            ].appId_children?.includes(element["appId"])
                ? targetArray[indexInData].appId_children
                : [
                      ...(targetArray[indexInData].appId_children || []),
                      element["appId"],
                  ],
            applicationName: targetArray[
                indexInData
            ].applicationName?.includes(element["Application"])
                ? targetArray[indexInData].applicationName
                : [
                      ...(targetArray[indexInData].applicationName || []),
                      element["Application"],
                  ],
            data: {
                ...targetArray[indexInData].data,
                ...peripheralObjects,
            },
        };
    }
}
